import { type Component, createMemo, mergeProps, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { FieldMessagePropsType } from '~/types/FieldMessage'
import { controlSize } from '~/utils/controlSize'

/**
 * Mensaje de error o de ayuda de un campo. Es el **único** sitio donde se
 * deciden su tipografía, su color, su sangría y su altura.
 *
 * Antes cada campo traía su propia copia del bloque, y entre los nueve había
 * cinco combinaciones distintas de alineación, margen, interlineado y truncado:
 * dos campos vecinos con el mismo mensaje no medían lo mismo, y el de un Input
 * quedaba a la izquierda mientras el del Select de al lado se centraba porque
 * heredaba el `text-align` del contenedor.
 *
 * Dos decisiones que valen para todos:
 *
 * - **La línea se reserva siempre.** Si el hueco apareciera al fallar la
 *   validación, el formulario daría un salto y en una rejilla una columna se
 *   quedaría más alta que la otra.
 * - **El mensaje se sangra hasta el texto del control.** Arrancaba en el borde
 *   del contenedor mientras el texto del campo empezaba detrás del relleno, así
 *   que nunca llegaba a estar alineado con nada.
 */
const FieldMessage: Component<FieldMessagePropsType> = props => {
  const merged = mergeProps({ indent: true, reserve: true }, props)
  const sizeConfig = createMemo(() => controlSize(merged.size))

  // `padStart` es un `start-*` (posición absoluta) porque lo usa la etiqueta
  // flotante; aquí hace falta el relleno equivalente.
  const indentClass = createMemo(() => (merged.indent ? sizeConfig().padX : ''))

  const textClasses = createMemo(() => twMerge('block truncate leading-tight', sizeConfig().messageText))

  return (
    <div id={merged.id} class={twMerge('mt-0.5 w-full text-start', merged.reserve ? sizeConfig().messageMinH : '', indentClass(), merged.class || '')} aria-live="polite">
      <Show when={merged.error}>
        <span title={merged.error} class={twMerge(textClasses(), 'text-danger')}>
          {merged.error}
        </span>
      </Show>
      <Show when={!merged.error && merged.helperText}>
        <span class={twMerge(textClasses(), 'font-light text-foreground-subtle')}>{merged.helperText}</span>
      </Show>
    </div>
  )
}

export default FieldMessage
